<template>
  <div>
    <Breadcrumbs main="Watches" title="Создание Watch видео"/>
    <div class="container-fluid">
      <b-card>
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h5>Список Watch видео</h5>
          <b-button variant="primary" @click="showCreateForm">Добавить видео</b-button>
        </div>

        <b-table
          responsive
          :items="watches"
          :fields="fields"
          :busy="isLoading"
        >
          <template #cell(actions)="row">
            <div class="d-flex justify-content-end align-items-center">
              <b-button size="sm" variant="primary" @click="editWatch(row.item)" class="mr-2">
                <i class="fa fa-edit"></i>
              </b-button>
              <b-button size="sm" variant="danger" @click="openDeleteModal(row.item.id)">
                <i class="fa fa-trash"></i>
              </b-button>
            </div>
          </template>
        </b-table>

        <b-modal v-model="showForm" :title="formMode === 'create' ? 'Добавить новое видео' : 'Редактировать видео'" size="lg">
          <b-form @submit.prevent="saveWatch">
            <div class="form-group">
              <label class="font-weight-bold">Ссылка на YouTube</label>
              <b-form-input
                v-model="currentWatch.youtube_link"
                placeholder="https://youtu.be/WxBX4N79r4w"
                :state="isYoutubeLinkValid"
              ></b-form-input>
              <b-form-invalid-feedback v-if="!isYoutubeLinkValid">
                Неверный URL YouTube
              </b-form-invalid-feedback>
            </div>

            <div class="form-group">
              <label class="font-weight-bold">Ссылка на Twitch</label>
              <b-form-input
                v-model="currentWatch.twitch_link"
                placeholder="https://www.twitch.tv/channel"
                :state="isTwitchLinkValid"
              ></b-form-input>
              <b-form-invalid-feedback v-if="!isTwitchLinkValid">
                Неверный URL Twitch
              </b-form-invalid-feedback>
            </div>

            <div class="form-group">
              <label class="font-weight-bold">Привязка к турниру</label>
              <div class="d-flex align-items-center">
                <b-form-input
                  v-model="tournamentSearch"
                  placeholder="Поиск турнира по названию..."
                  class="mr-2"
                  @input="onTournamentSearch"
                ></b-form-input>
                <b-button
                  v-if="currentWatch.tournament_id"
                  variant="outline-danger"
                  size="sm"
                  @click="unlinkTournament"
                  title="Отвязать турнир"
                >
                  <i class="fa fa-times"></i>
                </b-button>
              </div>
              <b-list-group v-if="showTournamentDropdown && filteredTournaments.length" class="tournament-dropdown mt-1">
                <b-list-group-item
                  v-for="t in filteredTournaments"
                  :key="t.id"
                  button
                  @click="selectTournament(t)"
                  class="py-2"
                >
                  <span class="font-weight-bold">{{ t.name }}</span>
                  <small class="text-muted ml-2">ID: {{ t.id }}</small>
                </b-list-group-item>
              </b-list-group>
              <div v-if="currentWatch.tournament_id" class="mt-2">
                <b-badge variant="primary" class="p-2">
                  Привязан к турниру: {{ selectedTournamentName }} (ID: {{ currentWatch.tournament_id }})
                </b-badge>
              </div>
            </div>

            <div class="form-group">
              <label class="font-weight-bold">Привязка к голосованию</label>
              <div class="d-flex align-items-center">
                <b-form-select
                  v-model="currentWatch.vote_id"
                  :options="voteOptions"
                ></b-form-select>
                <b-button
                  v-if="currentWatch.vote_id"
                  variant="outline-danger"
                  size="sm"
                  class="ml-2"
                  @click="currentWatch.vote_id = null"
                  title="Отвязать голосование"
                >
                  <i class="fa fa-times"></i>
                </b-button>
              </div>
              <div v-if="currentWatch.vote_id" class="mt-2">
                <b-badge variant="success" class="p-2">
                  Привязано голосование ID: {{ currentWatch.vote_id }}
                </b-badge>
              </div>
            </div>

            <b-tabs>
              <b-tab v-for="lang in languages" :key="lang.id" :title="lang.language.toUpperCase()">
                <div class="mt-3">
                  <label class="font-weight-bold">Название</label>
                  <b-form-input v-model="currentWatch.title[lang.language.toLowerCase()]" :placeholder="`Название ${lang.language.toUpperCase()}`"></b-form-input>
                </div>
                
                <div class="mt-3">
                  <label class="font-weight-bold">Meta Description {{ lang.language.toUpperCase() }}</label>
                  <b-form-input v-model="currentWatch.meta_description[lang.language.toLowerCase()]" :placeholder="`Meta Description ${lang.language.toUpperCase()}`"></b-form-input>
                </div>
                
                <div class="mt-3">
                  <label class="font-weight-bold">Описание</label>
                  <ckeditor :editor="editor" v-model="currentWatch.description[lang.language.toLowerCase()]" :config="editorConfig"></ckeditor>
                </div>
              </b-tab>
            </b-tabs>
          </b-form>
          <template #modal-footer>
            <b-button variant="secondary" @click="showForm = false">Отмена</b-button>
            <b-button variant="primary" @click="saveWatch" :disabled="!isFormValid">Сохранить</b-button>
          </template>
        </b-modal>
        <b-modal v-model="showDeleteModal" title="Удаление видео" hide-footer>
          <DeleteWatchVideo 
            :params="{ id: watchToDeleteId }" 
            @closeModal="closeDeleteModal"
          />
        </b-modal>
      </b-card>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { LanguageAdapterMixin } from "@/mixins/Language/LanguageAdapterMixin";
import DeleteWatchVideo from '@/components/modals/DeleteWatchVideo';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import CKEditor from '@ckeditor/ckeditor5-vue';
import api from '@/api/api';

export default {
  name: "CreateWatches",
  components: {
    DeleteWatchVideo,
    ckeditor: CKEditor.component
  },
  mixins: [LanguageAdapterMixin],
  data() {
    return {
      showForm: false,
      showDeleteModal: false,
      formMode: 'create',
      isLoading: false,
      fields: [
        { key: 'id', label: 'ID' },
        { key: 'youtube_link', label: 'YouTube ссылка' },
        { key: 'title.ru', label: 'Название (RU)' },
        { key: 'tournament.name', label: 'Турнир' },
        { key: 'actions', label: 'Действия' }
      ],
      currentWatch: this.getEmptyWatch(),
      tournaments: [],
      votes: [],
      tournamentSearch: '',
      showTournamentDropdown: false,
      selectedTournamentName: '',
      watchToDeleteId: null,
      editor: ClassicEditor,
      editorConfig: {
        toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote'],
        language: 'ru',
      }
    }
  },
  computed: {
    ...mapState('moduleWatch', ['watches']),
    isYoutubeLinkValid() {
      const regex = /^(https?\:\/\/)?(www\.youtube\.com|youtu\.?be)\/.+$/;
      return regex.test(this.currentWatch.youtube_link);
    },
    isTwitchLinkValid() {
      if (!this.currentWatch.twitch_link) return null;
      const regex = /^(https?:\/\/)?(www\.)?twitch\.tv\/.+$/;
      return regex.test(this.currentWatch.twitch_link);
    },
    isFormValid() {
      return this.isYoutubeLinkValid && this.isTwitchLinkValid !== false;
    },
    filteredTournaments() {
      if (!this.tournamentSearch) return [];
      return this.tournaments.slice(0, 10);
    },
    voteOptions() {
      return [
        { value: null, text: 'Без голосования' },
        ...this.votes.map(v => ({ value: v.id, text: v.title?.ru || `Голосование #${v.id}` }))
      ];
    }
  },
  methods: {
    ...mapActions('moduleWatch', ['fetchWatches', 'createWatch', 'updateWatch', 'deleteWatch']),
    getEmptyWatch() {
      return {
        youtube_link: '',
        twitch_link: '',
        tournament_id: null,
        vote_id: null,
        title: { ru: '', en: '', uz: '', kk: '' },
        meta_description: { ru: '', en: '', uz: '', kk: '' },
        description: { ru: '', en: '', uz: '', kk: '' }
      }
    },
    showCreateForm() {
      this.formMode = 'create';
      this.currentWatch = this.getEmptyWatch();
      this.showForm = true;
    },
    async onTournamentSearch() {
      this.showTournamentDropdown = this.tournamentSearch.length > 0;
      if (this.tournamentSearch.length >= 2) {
        try {
          const response = await api.tournament.index({ page: 1, search: this.tournamentSearch });
          this.tournaments = response.data.tournaments || [];
        } catch (error) {
          console.error('Error searching tournaments:', error);
        }
      }
    },
    selectTournament(tournament) {
      this.currentWatch.tournament_id = tournament.id;
      this.selectedTournamentName = tournament.name;
      this.tournamentSearch = '';
      this.showTournamentDropdown = false;
    },
    unlinkTournament() {
      this.currentWatch.tournament_id = null;
      this.selectedTournamentName = '';
      this.tournamentSearch = '';
    },
    editWatch(watch) {
      this.formMode = 'edit';
      this.currentWatch = {
        ...watch,
        tournament_id: watch.tournament?.id || null,
        vote_id: watch.vote?.id || null,
      };
      this.selectedTournamentName = watch.tournament?.name || '';
      if (!this.currentWatch.meta_description) {
        this.currentWatch.meta_description = { ru: '', en: '', uz: '', kk: '' };
      }
      this.showForm = true;
    },
    async saveWatch() {
      if (!this.isFormValid) {
        this.$toast.error('Неверный URL YouTube', { position: 'top-right' });
        return;
      }

      try {
        const watchData = { ...this.currentWatch };
        if (!watchData.vote_id) delete watchData.vote_id;
        if (!watchData.tournament_id) delete watchData.tournament_id;

        if (this.formMode === 'create') {
          await this.createWatch(watchData);
          this.$toast.success('Видео успешно добавлено', { position: 'top-right', timeout: 2000 });
        } else {
          await this.updateWatch({ id: this.currentWatch.id, watchData });
          this.$toast.success('Видео успешно обновлено', { position: 'top-right', timeout: 2000 });
        }
        this.showForm = false;
        await this.fetchWatches();
      } catch (error) {
        console.error('Error saving watch:', error);
        this.$toast.error('Ошибка при сохранении видео', { position: 'top-right', timeout: 2000 });
      }
    },
    openDeleteModal(id) {
      this.watchToDeleteId = id;
      this.showDeleteModal = true;
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.watchToDeleteId = null;
      this.fetchWatches();
    },
    async loadVotes() {
      try {
        const response = await api.votes.index();
        this.votes = response.data.data || response.data || [];
      } catch (error) {
        console.error('Error loading votes:', error);
      }
    }
  },
  created() {
    this.isLoading = true;
    this.fetchWatches().finally(() => {
      this.isLoading = false;
    });
    this.loadVotes();
    this.grabLanguages();
  },
}
</script>

<style scoped>
.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
}

.tournament-dropdown {
  max-height: 250px;
  overflow-y: auto;
  position: absolute;
  z-index: 100;
  width: calc(100% - 30px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.tournament-dropdown .list-group-item:hover {
  background-color: #f0f4ff;
}

.form-control {
  background-color: #f8f9fa;
  border: 1px solid #ced4da;
}

.nav-tabs {
  border-bottom: 1px solid #dee2e6;
}

.nav-tabs .nav-link {
  margin-bottom: -1px;
  border: 1px solid transparent;
  border-top-left-radius: 0.25rem;
  border-top-right-radius: 0.25rem;
}

.nav-tabs .nav-link.active {
  color: #495057;
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 #fff;
}

.table td {
  vertical-align: middle;
}

@media (max-width: 767.98px) {
  .table td {
    padding: 0.5rem;
  }

  .btn-sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
  }
}

.ck-editor__editable {
  min-height: 300px;
}
</style>